import React from 'react';
import PropTypes from 'prop-types';
import {useHistory} from 'react-router-dom';
import StarRoundedIcon from '@material-ui/icons/StarRounded';
import CheckIcon from '@material-ui/icons/Check';
import {DashboardTitle} from 'routes/App/dashboard/styles';
import {ReactComponent as IconNeedsReply} from 'assets/mainPage/customerReviews/icon-needs-reply.svg';
import {ReactComponent as IconReviewsWeek} from 'assets/mainPage/customerReviews/icon-reviews-week.svg';
import {ReactComponent as IconOverallRating} from 'assets/mainPage/customerReviews/icon-overall-rating.svg';
import {ReactComponent as IconReviewsBar} from 'assets/mainPage/customerReviews/icon-reviews-bar.svg';
import {formatCount} from 'routes/App/MainPage/Components/GrowthPrograms/utils';
import {useFeedbackSummary} from 'store/feedback/selectors';
import {
  ActionPill,
  CaughtUp,
  ColumnHeader,
  ColumnIcon,
  ColumnValue,
  RatingSub,
  ReplyButton,
  ReviewsCard,
  ReviewsColumn,
  ValueCaption
} from 'routes/App/MainPage/Components/CustomerReviews/styles';
import {ViewAllRow, ViewAllRowLeft} from 'routes/App/MainPage/styles';
import {ButtonOutline} from 'components/buttons';

const formatRating = (value) =>
  new Intl.NumberFormat('en-GB', {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1
  }).format(Number(value) || 0);

const ReplyIcon = () => (
  <svg width='15' height='13' viewBox='0 0 16 14' aria-hidden='true'>
    <path d='M6 1L1 6l5 5V8c4 0 7 1 9 5 0-5-3-8-9-8z' fill='currentColor' />
  </svg>
);

const CustomerReviews = ({reviewsThisWeek, reviewsAllTime, averageRating}) => {
  const history = useHistory();
  const summary = useFeedbackSummary();
  const parsedNeedsReply = Number(summary?.needsReplyCount);
  const needsReply = Number.isFinite(parsedNeedsReply) ? parsedNeedsReply : 0;
  const hasPending = needsReply >= 1;
  const isCaughtUp = summary != null && !hasPending;
  const customerLabel = needsReply === 1 ? 'customer' : 'customers';
  const allTimeLabel = `${formatCount(reviewsAllTime)} all-time`;

  const openReviews = () => {
    history.push('/feedback');
  };

  return (
    <>
      <DashboardTitle className='WithLine2px title24 titleBold MainPageTitle'>
        Customer Reviews
      </DashboardTitle>
      <ReviewsCard>
        <ReviewsColumn className={hasPending ? 'pending' : undefined}>
          <ColumnHeader>
            <ColumnIcon>
              <IconNeedsReply aria-hidden='true' />
            </ColumnIcon>
            Needs Reply
            {hasPending && <ActionPill>Action needed</ActionPill>}
          </ColumnHeader>
          <ColumnValue>
            <span>{formatCount(needsReply)}</span>
            <ValueCaption>
              {hasPending && `${customerLabel} waiting to hear from you`}
              {isCaughtUp && 'no one waiting'}
            </ValueCaption>
          </ColumnValue>
          {hasPending && (
            <ReplyButton type='button' onClick={openReviews}>
              <ReplyIcon />
              Reply to {formatCount(needsReply)} {customerLabel}
            </ReplyButton>
          )}
          {isCaughtUp && (
            <CaughtUp>
              <CheckIcon style={{color: '#1fb57a'}} /> All caught up
            </CaughtUp>
          )}
        </ReviewsColumn>
        <ReviewsColumn>
          <ColumnHeader>
            <ColumnIcon>
              <IconReviewsWeek aria-hidden='true' />
            </ColumnIcon>
            Reviews This Week
          </ColumnHeader>
          <ColumnValue>
            <span>{formatCount(reviewsThisWeek)}</span>
            <ValueCaption>{allTimeLabel}</ValueCaption>
          </ColumnValue>
        </ReviewsColumn>
        <ReviewsColumn>
          <ColumnHeader>
            <ColumnIcon>
              <IconOverallRating aria-hidden='true' />
            </ColumnIcon>
            Overall Rating
          </ColumnHeader>
          <ColumnValue>
            {formatRating(averageRating)}
            <StarRoundedIcon style={{color: '#f5b50a'}} />
          </ColumnValue>
          <RatingSub>
            Based on {formatCount(reviewsAllTime)}{' '}
            {Number(reviewsAllTime) === 1 ? 'review' : 'reviews'}
          </RatingSub>
        </ReviewsColumn>
      </ReviewsCard>
      <ViewAllRow>
        <ViewAllRowLeft className='darkIcon'>
          <IconReviewsBar aria-hidden='true' />
          <span>View all your performance stats</span>
        </ViewAllRowLeft>
        <ButtonOutline onClick={openReviews} className='ButtonSmallXs'>
          View all
        </ButtonOutline>
      </ViewAllRow>
    </>
  );
};

CustomerReviews.propTypes = {
  reviewsThisWeek: PropTypes.number,
  reviewsAllTime: PropTypes.number,
  averageRating: PropTypes.number
};

CustomerReviews.defaultProps = {
  reviewsThisWeek: 0,
  reviewsAllTime: 0,
  averageRating: 0
};

export default CustomerReviews;
